import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CategoryColorMap, Post, Payment, User } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import './Admin.css';

const defaultCategoryColors: CategoryColorMap = {
  'fire-alarm': { background: '#ffeeee', text: '#cc3333' },
  sprinkler: { background: '#ddeeff', text: '#3366cc' },
  pavement: { background: '#ffeedd', text: '#996633' },
  maintenance: { background: '#ddffee', text: '#33cc66' },
  announcement: { background: '#eeeeff', text: '#6633cc' },
  other: { background: '#eeeeee', text: '#666666' },
};

// Convert 3-char hex to 6-char hex for color picker compatibility
const expandHex = (hex: string): string => {
  if (!hex) return '#000000';
  const cleaned = hex.replace('#', '');
  if (cleaned.length === 3) {
    return '#' + cleaned.split('').map(c => c + c).join('');
  }
  if (cleaned.length === 6) {
    return '#' + cleaned;
  }
  return '#000000';
};

const defaultCategories = ['fire-alarm', 'sprinkler', 'pavement', 'maintenance', 'announcement', 'other'];

const Admin: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'posts' | 'payments' | 'users'>('posts');
  const [posts, setPosts] = useState<Post[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [categoryColors, setCategoryColors] = useState<CategoryColorMap>(defaultCategoryColors);
  const [colorsLoading, setColorsLoading] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments`);
      setPayments(response.data.payments);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const fetchCategoryColors = useCallback(async () => {
    try {
      setColorsLoading(true);
      const response = await axios.get(`${API_URL}/api/posts/category-colors`);
      setCategoryColors({
        ...defaultCategoryColors,
        ...(response.data.colors || {}),
      });
    } catch (error) {
      console.error('Failed to fetch category colors:', error);
      setCategoryColors(defaultCategoryColors);
    } finally {
      setColorsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'posts') {
      fetchPosts();
      fetchCategoryColors();
    } else if (activeTab === 'payments') {
      fetchPayments();
    } else if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, fetchPosts, fetchPayments, fetchUsers, fetchCategoryColors]);

  const handleDeletePost = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await axios.delete(`${API_URL}/api/posts/${id}`);
        fetchPosts();
      } catch (error) {
        console.error('Failed to delete post:', error);
      }
    }
  };

  const handleDeletePayment = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await axios.delete(`${API_URL}/api/payments/${id}`);
        fetchPayments();
      } catch (error) {
        console.error('Failed to delete payment:', error);
      }
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API_URL}/api/users/${id}`);
        fetchUsers();
      } catch (error) {
        console.error('Failed to delete user:', error);
      }
    }
  };

  const handleSaveCategoryColors = async () => {
    try {
      setSavingColors(true);
      await axios.put(`${API_URL}/api/posts/category-colors`, { colors: categoryColors });
      alert('Category colors updated');
    } catch (error) {
      console.error('Failed to update category colors:', error);
      alert('Failed to update category colors. Please try again.');
    } finally {
      setSavingColors(false);
    }
  };

  const handleAddCategory = () => {
    const categoryKey = newCategoryName.toLowerCase().trim().replace(/\s+/g, '-');
    if (!categoryKey) {
      alert('Please enter a category name');
      return;
    }
    if (categoryColors[categoryKey]) {
      alert('This category already exists');
      return;
    }
    setCategoryColors({
      ...categoryColors,
      [categoryKey]: { background: '#eeeeee', text: '#333333' },
    });
    setNewCategoryName('');
    setShowAddCategory(false);
  };

  const handleDeleteCategory = async (category: string) => {
    if (defaultCategories.includes(category)) {
      alert('Cannot delete default categories');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the "${category}" category?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/posts/category-colors/${encodeURIComponent(category)}`);
      const { [category]: _, ...remaining } = categoryColors;
      setCategoryColors(remaining);
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Failed to delete category');
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Manage building content, users, and payments</p>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'posts' ? 'active' : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          Posts Management
        </button>
        <button
          className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          Payments Management
        </button>
        <button
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users Management
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'posts' && (
          <div className="posts-section">
            <div className="section-header">
              <h2>Posts</h2>
              <button className="add-btn" onClick={() => setShowPostModal(true)}>
                + Add New Post
              </button>
            </div>
            <div className="category-colors-panel">
              <div className="category-colors-header">
                <h3>Manage Categories & Colors</h3>
                <div className="category-header-actions">
                  <button
                    className="add-category-btn"
                    onClick={() => setShowAddCategory(!showAddCategory)}
                  >
                    + Add Category
                  </button>
                  <button
                    className="save-btn"
                    onClick={handleSaveCategoryColors}
                    disabled={savingColors || colorsLoading}
                  >
                    {savingColors ? 'Saving...' : 'Save Colors'}
                  </button>
                </div>
              </div>
              {showAddCategory && (
                <div className="add-category-form">
                  <input
                    type="text"
                    placeholder="New category name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                  />
                  <button onClick={handleAddCategory}>Add</button>
                  <button onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}>Cancel</button>
                </div>
              )}
              {colorsLoading ? (
                <p className="colors-loading">Loading current colors...</p>
              ) : (
                <div className="color-grid">
                  {Object.entries(categoryColors).map(([category, colors]) => (
                    <div key={category} className="color-row">
                      <span className="color-name">{category.replace(/-/g, ' ')}</span>
                      <label className="color-input">
                        <span>Background</span>
                        <input
                          type="color"
                          value={expandHex(colors.background)}
                          onChange={(e) =>
                            setCategoryColors({
                              ...categoryColors,
                              [category]: {
                                ...colors,
                                background: e.target.value,
                              },
                            })
                          }
                        />
                      </label>
                      <label className="color-input">
                        <span>Text</span>
                        <input
                          type="color"
                          value={expandHex(colors.text)}
                          onChange={(e) =>
                            setCategoryColors({
                              ...categoryColors,
                              [category]: {
                                ...colors,
                                text: e.target.value,
                              },
                            })
                          }
                        />
                      </label>
                      <span
                        className="color-preview"
                        style={{
                          backgroundColor: colors.background,
                          color: colors.text,
                        }}
                      >
                        Aa
                      </span>
                      {!defaultCategories.includes(category) && (
                        <button
                          className="delete-category-btn"
                          onClick={() => handleDeleteCategory(category)}
                          title="Delete category"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="admin-table">
              {posts.map((post) => (
                <div key={post._id} className="admin-card">
                  <div className="card-content">
                    <h3>{post.title}</h3>
                    <p className="card-category">{post.category.replace('-', ' ')}</p>
                    <p className="card-description">{post.description?.replace(/^[•\-*]\s*/gm, '')}</p>
                    {(() => {
                      const attachments =
                        post.attachments && post.attachments.length > 0
                          ? post.attachments
                          : post.attachment
                          ? [post.attachment]
                          : [];
                      const safe = attachments.filter(
                        (a) => !!a && (!!a.filename || !!(a as any)._id)
                      );
                      if (safe.length === 0) return null;
                      return (
                        <div className="card-attachment">
                          <p>
                            Attachments: {safe.length}
                          </p>
                          <ul>
                            {safe.map((a) => (
                              <li key={a.filename || (a as any)._id}>
                                {a.originalName || a.filename || 'Attachment'}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                    <p className="card-meta">
                      By: {post.createdBy.firstName} {post.createdBy.lastName} | 
                      Updated: {new Date(post.lastUpdated).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => {
                        setEditingPost(post);
                        setShowPostModal(true);
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeletePost(post._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="payments-section">
            <div className="section-header">
              <h2>Payments</h2>
              <button className="add-btn" onClick={() => setShowPaymentModal(true)}>
                + Add New Payment
              </button>
            </div>
            <div className="admin-table">
              {payments.map((payment) => (
                <div key={payment._id} className="admin-card">
                  <div className="card-content">
                    <h3>{payment.title}</h3>
                    <p className="card-amount">${payment.amount}</p>
                    <p className="card-description">{payment.description?.replace(/^[•\-*]\s*/gm, '')}</p>
                    <p className="card-meta">
                      {payment.user.firstName} {payment.user.lastName} 
                      {payment.apartment && ` (Unit ${payment.apartment})`} | 
                      Due: {new Date(payment.dueDate).toLocaleDateString()} | 
                      Status: <span className={`status-${payment.status}`}>{payment.status}</span>
                    </p>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeletePayment(payment._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h2>Users</h2>
              {currentUser?.role === 'admin' && (
                <button className="add-btn" onClick={() => setShowUserModal(true)}>
                  + Add User
                </button>
              )}
            </div>
            <div className="admin-table">
              {users.map((user) => (
                <div key={user.id} className="admin-card">
                  <div className="card-content">
                    <h3>{user.firstName} {user.lastName}</h3>
                    <p className="card-email">{user.email}</p>
                    <p className="card-meta">
                      Role: {user.role} 
                      {user.apartment && ` | Unit: ${user.apartment}`}
                      {user.phoneNumber && ` | Phone: ${user.phoneNumber}`}
                      {user.mustChangePassword && ` | Temp password (needs reset)`}
                    </p>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showPostModal && (
        <PostModal
          post={editingPost}
          categories={Object.keys(categoryColors)}
          onClose={() => {
            setShowPostModal(false);
            setEditingPost(null);
          }}
          onSuccess={() => {
            setShowPostModal(false);
            setEditingPost(null);
            fetchPosts();
          }}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          users={users}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            fetchPayments();
          }}
        />
      )}

      {showUserModal && (
        <UserModal
          onClose={() => setShowUserModal(false)}
          onSuccess={() => {
            setShowUserModal(false);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

// Post Modal Component
const PostModal: React.FC<{
  post: Post | null;
  categories: string[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ post, categories, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    category: post?.category || 'maintenance',
    description: post?.description || '',
    responsibleParty: post?.responsibleParty || '',
    contactInfo: post?.contactInfo || '',
    lastUpdated: post?.lastUpdated ? new Date(post.lastUpdated).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    nextScheduledDate: post?.nextScheduledDate ? new Date(post.nextScheduledDate).toISOString().split('T')[0] : '',
  });
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [currentAttachments, setCurrentAttachments] = useState<
    NonNullable<Post['attachments']>
  >(() => {
    if (post?.attachments && post.attachments.length > 0) return post.attachments;
    if (post?.attachment) return [post.attachment];
    return [];
  });

  const handleRemoveExistingAttachment = async (identifier: string) => {
    if (!post?._id) return;
    if (!window.confirm('Remove this attachment?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/posts/${post._id}/attachments/${encodeURIComponent(identifier)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentAttachments((prev) =>
        prev.filter((a) => (a.filename || a._id) !== identifier)
      );
      alert('Attachment removed successfully');
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      alert('Failed to remove attachment');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('responsibleParty', formData.responsibleParty);
      formDataToSend.append('contactInfo', formData.contactInfo);
      formDataToSend.append('lastUpdated', formData.lastUpdated);
      if (formData.nextScheduledDate) {
        formDataToSend.append('nextScheduledDate', formData.nextScheduledDate);
      }
      newAttachments.forEach((file) => {
        formDataToSend.append('attachments', file);
      });

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      };

      if (post) {
        await axios.put(`${API_URL}/api/posts/${post._id}`, formDataToSend, config);
      } else {
        await axios.post(`${API_URL}/api/posts`, formDataToSend, config);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save post:', error);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{post ? 'Edit Post' : 'Create New Post'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="modal-form-content">
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
                rows={8}
            />
          </div>
          <div className="form-group">
            <label>Responsible Party</label>
            <input
              type="text"
              value={formData.responsibleParty}
              onChange={(e) => setFormData({ ...formData, responsibleParty: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Contact Info</label>
            <input
              type="text"
              value={formData.contactInfo}
              onChange={(e) => setFormData({ ...formData, contactInfo: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Last Updated</label>
            <input
              type="date"
              value={formData.lastUpdated}
              onChange={(e) => setFormData({ ...formData, lastUpdated: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Next Scheduled Date (optional)</label>
            <input
              type="date"
              value={formData.nextScheduledDate}
              onChange={(e) => setFormData({ ...formData, nextScheduledDate: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Attachment (optional)</label>
            <input
              type="file"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length === 0) return;
                setNewAttachments((prev) => [...prev, ...files]);
                // allow re-selecting the same file name again
                e.currentTarget.value = '';
              }}
            />
            {post?._id && currentAttachments.length > 0 && (
              <div className="current-attachment">
                <p className="current-file">Current attachments:</p>
                <ul>
                  {currentAttachments
                    .filter((a) => !!a && (!!a.filename || !!a._id))
                    .map((a) => (
                    <li key={a.filename || a._id}>
                      <a
                        href={`${API_URL}${a.url}`}
                        download={a.originalName || a.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {a.originalName || a.filename || 'Attachment'}
                      </a>
                      <button
                        type="button"
                        className="remove-attachment-btn"
                        onClick={() =>
                          handleRemoveExistingAttachment(a.filename || a._id || '')
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {newAttachments.length > 0 && (
              <div className="current-attachment">
                <p className="new-file">New files to upload:</p>
                <ul>
                  {newAttachments.map((f, idx) => (
                    <li key={`${f.name}-${f.size}-${idx}`}>
                        <span>{f.name}</span>
                      <button
                        type="button"
                        className="remove-attachment-btn"
                        onClick={() =>
                          setNewAttachments((prev) => prev.filter((_, i) => i !== idx))
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              {post ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Payment Modal Component
const PaymentModal: React.FC<{
  users: User[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ users, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    dueDate: '',
    user: '',
    apartment: '',
  });

  useEffect(() => {
    if (users.length === 0) {
      axios.get(`${API_URL}/api/users`).then(res => {
        // Users will be available in parent component
      });
    }
  }, [users]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/payments`, {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create payment:', error);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Create New Payment</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Due Date</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>User</label>
            <select
              value={formData.user}
              onChange={(e) => {
                const selectedUser = users.find(u => u.id === e.target.value);
                setFormData({ 
                  ...formData, 
                  user: e.target.value,
                  apartment: selectedUser?.apartment || ''
                });
              }}
              required
            >
              <option value="">Select a user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} {user.apartment && `(Unit ${user.apartment})`}
                </option>
              ))}
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Admin;

const UserModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'renter' as User['role'],
    apartment: '',
    phoneNumber: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/users`, {
        ...formData,
        apartment: formData.apartment || undefined,
        phoneNumber: formData.phoneNumber || undefined,
      });

      const temp = response.data.temporaryPassword;
      if (temp) {
        window.alert(`User created.\n\nTemporary password: ${temp}\n\nShare this with the user; they will be forced to change it on first login.`);
      } else {
        window.alert('User created.');
      }

      onSuccess();
    } catch (error: any) {
      console.error('Failed to create user:', error);
      const msg = error?.response?.data?.message || 'Failed to create user';
      window.alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Create New User</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
              required
            >
              <option value="renter">renter</option>
              <option value="owner">owner</option>
              <option value="trustee">trustee</option>
              <option value="admin">admin</option>
            </select>
          </div>

          <div className="form-group">
            <label>Apartment (optional)</label>
            <input
              type="text"
              value={formData.apartment}
              onChange={(e) => setFormData({ ...formData, apartment: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Phone (optional)</label>
            <input
              type="text"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

